import React, { useState } from 'react';
import {
  Box,
  Button,
  Drawer,
  Grid,
  TextField,
  Typography,
  IconButton,
  Divider
} from '@mui/material';
import { addTesSection, updateTesSection, deleteTesSection, AddTesSectionPayload, TesSectionApi } from '../../services/estimateService';

interface AddTesSectionDialogProps {
  open: boolean;
  estimateId: number;
  onClose: () => void;
  onSaved?: (section: TesSectionApi) => void;
  section?: TesSectionApi; // jeżeli przekazane, dialog w trybie edycji
  existingNumbers?: string[];
  lastPositionNr?: string; // Numer ostatniej pozycji dla domyślnej wartości
}

const AddTesSectionDialog: React.FC<AddTesSectionDialogProps> = ({ open, estimateId, onClose, onSaved, section, existingNumbers = [], lastPositionNr }) => {
  const [nr, setNr] = useState(section?.nr || '');
  const [rangeFrom, setRangeFrom] = useState(section ? section.range.split('-')[0] : '');
  const [rangeTo, setRangeTo] = useState(section ? section.range.split('-')[1] : '');
  const [name, setName] = useState(section?.name || '');
  const [insertAfterNr, setInsertAfterNr] = useState('');
  const [saving, setSaving] = useState(false);

  const isDuplicateNr = () => {
    if (!nr) return false;
    if (section && nr === section.nr) return false; // unchanged
    return existingNumbers.includes(nr);
  };

  // Gdy dialog otwierany z nową sekcją, zaktualizuj pola
  React.useEffect(() => {
    if (open) {
      setNr(section?.nr || '');
      setRangeFrom(section ? section.range.split('-')[0] : '');
      setRangeTo(section ? section.range.split('-')[1] : '');
      setName(section?.name || '');
      setInsertAfterNr(lastPositionNr || '');
    }
  }, [open, section, lastPositionNr]);

  const reset = () => {
    setNr('');
    setRangeFrom('');
    setRangeTo('');
    setName('');
    setInsertAfterNr('');
  };

  const handleSave = async () => {
    if (!nr || !rangeFrom || !rangeTo || !name) return;
    const payload: AddTesSectionPayload = {
      nr,
      rangeFrom: parseInt(rangeFrom, 10),
      rangeTo: parseInt(rangeTo, 10),
      name,
    };
    try {
      setSaving(true);
      let saved: TesSectionApi;
      if(section){
        // edit
        saved = await updateTesSection(estimateId, section.id, payload);
      } else {
        // create
        saved = await addTesSection(estimateId, payload);
      }
      onSaved && onSaved(saved);
      reset();
      onClose();
    } catch (err) {
      console.error('[AddTesSectionDialog] save error', err);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      reset();
      onClose();
    }
  };

  const handleDelete = async () => {
    if (!section) return;
    if (!window.confirm('Czy na pewno chcesz usunąć ten dział TES?')) return;
    
    try {
      setSaving(true);
      await deleteTesSection(estimateId, section.id);
      onSaved && onSaved(section);
      reset();
      onClose();
    } catch (err) {
      console.error('[AddTesSectionDialog] delete error', err);
      alert('Nie udało się usunąć działu TES');
    } finally {
      setSaving(false);
    }
  };

  // Style pól tekstowych jak w AddProjectForm
  const textFieldSx = {
    '& .MuiInputLabel-root': {
      fontFamily: 'Roboto, sans-serif',
      fontWeight: 400,
      fontSize: '12px',
      lineHeight: '16px',
      color: '#2D2D32'
    },
    '& .MuiOutlinedInput-root': {
      height: 32,
      backgroundColor: '#fff',
      borderRadius: '4px'
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' },
    '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
    '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
    '& .MuiOutlinedInput-root.Mui-error:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
    '& .MuiOutlinedInput-root.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' },
    '& .MuiInputBase-input': {
      padding: '8px 12px',
      boxSizing: 'border-box',
      fontFamily: 'Roboto, sans-serif',
      fontSize: '16px',
      color: '#2D2D32'
    },
    '& .MuiFormHelperText-root': { ml: 0 }
  } as const;

  // Komponent FieldLabel jak w AddProjectForm
  const FieldLabel: React.FC<{ text: string; required?: boolean }> = ({ text, required }) => (
    <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '20px', color: '#2D2D32', mb: 0.5 }}>
      {text}
      {required && <Box component="span" sx={{ color: '#d32f2f' }}> *</Box>}
    </Typography>
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{ sx: { width: 450, maxWidth: '100vw', borderRadius: 0, height: '100vh' } }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Nagłówek */}
        <Box sx={{ pl: 4, pr: 4, pt: '22px', pb: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '18px', color: '#2D2D32' }}>
            {section ? 'Edytuj dział TES' : 'Dodaj dział TES'}
          </Typography>
          <IconButton 
            onClick={handleClose} 
            size="small" 
            disabled={saving}
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 30,
              minHeight: 30,
              '& > *': {
                pointerEvents: 'none',
              },
            }}
          >
            <Box 
              component="img" 
              src="/assets/close.svg" 
              alt="Zamknij" 
              sx={{ 
                width: 13, 
                height: 13, 
                display: 'block',
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
              }} 
            />
          </IconButton>
        </Box>

        {/* Zawartość formularza */}
        <Box sx={{ flex: 1, overflowY: 'auto', px: 4, py: 3 }}>
          <Grid container rowSpacing={1.5} columnSpacing={3}>
            <Grid item xs={12}>
              <FieldLabel text="Nr" required />
              <TextField
                fullWidth
                value={nr}
                onChange={e => setNr(e.target.value)}
                variant="outlined"
                size="small"
                sx={textFieldSx}
                error={isDuplicateNr()}
                helperText={isDuplicateNr() ? 'Duplikat nr' : ''}
              />
            </Grid>
            <Grid item xs={6}>
              <FieldLabel text="Pozycje od" required />
              <TextField
                fullWidth
                value={rangeFrom}
                onChange={e => setRangeFrom(e.target.value)}
                variant="outlined"
                size="small"
                sx={textFieldSx}
              />
            </Grid>
            <Grid item xs={6}>
              <FieldLabel text="Pozycje do" required />
              <TextField
                fullWidth
                value={rangeTo}
                onChange={e => setRangeTo(e.target.value)}
                variant="outlined"
                size="small"
                sx={textFieldSx}
              />
            </Grid>
            <Grid item xs={12}>
              <FieldLabel text="Opis działu" required />
              <TextField
                fullWidth
                value={name}
                onChange={e => setName(e.target.value)}
                variant="outlined"
                size="small"
                sx={textFieldSx}
              />
            </Grid>
            <Grid item xs={12}>
              <FieldLabel text="Wstaw za pozycją" />
              <TextField
                fullWidth
                value={insertAfterNr}
                onChange={e => setInsertAfterNr(e.target.value)}
                variant="outlined"
                size="small"
                sx={textFieldSx}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Stopka z przyciskami */}
        <Divider />
        <Box sx={{ px: 4, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button 
            onClick={section ? handleDelete : handleClose} 
            variant="text" 
            disabled={saving || (!section && false)}
            sx={{ 
              fontFamily: 'Roboto, sans-serif', 
              fontWeight: 400, 
              fontSize: '14px', 
              color: '#d32f2f', 
              textTransform: 'none' 
            }}
          >
            Usuń
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button 
              onClick={handleClose} 
              variant="text" 
              disabled={saving}
              sx={{ 
                fontFamily: 'Roboto, sans-serif', 
                fontWeight: 400, 
                fontSize: '14px', 
                color: '#2D2D32', 
                textTransform: 'none' 
              }}
            >
              Anuluj
            </Button>
            <Button 
              onClick={handleSave} 
              variant="contained" 
              disabled={saving || !nr || !rangeFrom || !rangeTo || !name || isDuplicateNr()}
              sx={{ 
                backgroundColor: '#020242',
                fontFamily: 'Roboto, sans-serif',
                fontWeight: 500,
                fontSize: '14px',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#030360'
                }
              }}
            >
              {section ? 'Aktualizuj' : 'Zapisz'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default AddTesSectionDialog; 